import { useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { getDeviceCategoryLabel, getDeviceCategoryOrderLabel, getEfemPeripheralReassignState } from '@cnapse/shared';
import * as dt from '@cnapse/shared/data.types.js';
import type { DeviceInventoryStatus, TenantDeviceRecord } from '@cnapse/shared/schemas.js';
import { AppButton, AppTextField } from '../../design-system';
import { EfemSetupList, EfemSetupListEmpty, EfemSetupListRow } from './EfemSetupList';
import { EfemSetupSection } from './EfemSetupSection';
import type { WaferCameraEditRow } from './efemGatewaySetup.types';

export type { WaferCameraEditRow };

const WAFER_ID_MODULES_LABEL = getDeviceCategoryOrderLabel('waferid-camera');
const WAFER_ID_MODULE_LABEL = getDeviceCategoryLabel('waferid-camera');

interface WaferIdCameraSectionProps {
  rows: WaferCameraEditRow[];
  onRowsChange: (rows: WaferCameraEditRow[]) => void;
  allCameras: TenantDeviceRecord[];
  gatewaySerialNumber: string;
  tenantGatewayStatusBySerial: ReadonlyMap<string, DeviceInventoryStatus | undefined>;
  disabled: boolean;
  loading?: boolean;
}

const WAFER_SIZES = Object.values(dt.WAFER_SIZE);

const EMPTY_FORM: WaferCameraEditRow = {
  roleKey: '',
  roleLabel: '',
  boundSerial: '',
  displayLabel: '',
  idSide: 'top',
  mountCalibrationDeg: 0,
  supportedWaferSize: dt.WAFER_SIZE.Size300mm,
  configured: false,
};

function mountLabel(idSide: 'top' | 'bottom'): string {
  return idSide === 'bottom' ? 'Below wafer' : 'Above wafer';
}

function cameraModel(serial: string, allCameras: TenantDeviceRecord[]): string | undefined {
  return allCameras.find((c) => c.serialNumber === serial)?.deviceModel ?? undefined;
}

export function WaferIdCameraSection({
  rows,
  onRowsChange,
  allCameras,
  gatewaySerialNumber,
  tenantGatewayStatusBySerial,
  disabled,
  loading = false,
}: WaferIdCameraSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<WaferCameraEditRow>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const openAddDialog = () => {
    const idx = rows.length + 1;
    setEditingKey(null);
    setForm({
      roleKey: `new-camera-${idx}`,
      roleLabel: `Module ${idx}`,
      boundSerial: '',
      displayLabel: '',
      idSide: 'top',
      mountCalibrationDeg: 0,
      supportedWaferSize: dt.WAFER_SIZE.Size300mm,
      configured: false,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (row: WaferCameraEditRow) => {
    setEditingKey(row.roleKey);
    setForm({ ...row });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleDialogApply = () => {
    if (!form.boundSerial) {
      setFormError(`${WAFER_ID_MODULE_LABEL} device is required`);
      return;
    }
    const duplicateRow = rows.some((r) => r.boundSerial === form.boundSerial && r.roleKey !== editingKey);
    if (duplicateRow) {
      setFormError(`This ${WAFER_ID_MODULE_LABEL.toLowerCase()} is already assigned`);
      return;
    }

    const camera = allCameras.find((c) => c.serialNumber === form.boundSerial);
    if (camera) {
      const reassign = getEfemPeripheralReassignState({
        parentGatewaySerial: camera.parentGatewaySerialNumber,
        currentGatewaySerial: gatewaySerialNumber,
        tenantGatewayStatusBySerial,
      });
      if (reassign.requiresMoveConfirm) {
        const fromGateway = camera.parentGatewaySerialNumber ?? 'another gateway';
        if (
          !window.confirm(
            `${form.boundSerial} is currently commissioned on gateway ${fromGateway}. `
            + 'Move it to this gateway when you save?',
          )
        ) {
          return;
        }
      }
    }

    const nextRows = editingKey
      ? rows.map((r) => (r.roleKey === editingKey ? { ...form, roleKey: editingKey, configured: true } : r))
      : [...rows, { ...form, configured: true }];

    onRowsChange(nextRows);
    setDialogOpen(false);
  };

  const handleDelete = (row: WaferCameraEditRow) => {
    if (!window.confirm(`Remove ${WAFER_ID_MODULE_LABEL.toLowerCase()} ${row.displayLabel || row.roleLabel}?`)) return;
    onRowsChange(rows.filter((r) => r.roleKey !== row.roleKey));
  };

  return (
    <EfemSetupSection
      title={WAFER_ID_MODULES_LABEL}
      statusLabel={rows.length > 0 ? `${rows.length} module${rows.length !== 1 ? 's' : ''}` : undefined}
      statusVariant={rows.length > 0 ? 'success' : 'warning'}
      disabled={disabled}
      disabledHint={`Enter a gateway name above to configure ${WAFER_ID_MODULES_LABEL.toLowerCase()}.`}
      headerAction={
        <AppButton size="small" variant="contained" startIcon={<Add />} onClick={openAddDialog}>
          Add module
        </AppButton>
      }
    >
      {loading ? (
        <Box py={1}>
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Loading…
          </Alert>
        </Box>
      ) : rows.length === 0 ? (
        <EfemSetupListEmpty
          message={`No ${WAFER_ID_MODULES_LABEL.toLowerCase()} configured.`}
          action={
            <AppButton size="small" variant="outlined" startIcon={<Add />} onClick={openAddDialog}>
              Add module
            </AppButton>
          }
        />
      ) : (
        <EfemSetupList>
          {rows.map((row, index) => (
            <EfemSetupListRow
              key={row.roleKey}
              primary={row.displayLabel || row.roleLabel}
              secondary={row.boundSerial}
              meta={`${mountLabel(row.idSide)} · ${row.supportedWaferSize} · ${row.mountCalibrationDeg}°${cameraModel(row.boundSerial, allCameras) ? ` · ${cameraModel(row.boundSerial, allCameras)}` : ''}`}
              onEdit={() => openEditDialog(row)}
              editLabel={`Edit ${WAFER_ID_MODULE_LABEL.toLowerCase()}`}
              onDelete={() => handleDelete(row)}
              deleteLabel={`Remove ${WAFER_ID_MODULE_LABEL.toLowerCase()}`}
              isLast={index === rows.length - 1}
            />
          ))}
        </EfemSetupList>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingKey ? `Edit ${WAFER_ID_MODULE_LABEL.toLowerCase()}` : `Add ${WAFER_ID_MODULE_LABEL.toLowerCase()}`}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} pt={0.5}>
            <FormControl size="small" fullWidth required>
              <InputLabel>{WAFER_ID_MODULE_LABEL} device</InputLabel>
              <Select
                label={`${WAFER_ID_MODULE_LABEL} device`}
                value={form.boundSerial}
                onChange={(e) => setForm((prev) => ({ ...prev, boundSerial: e.target.value }))}
              >
                <MenuItem value="" disabled>
                  {`Select a ${WAFER_ID_MODULE_LABEL.toLowerCase()}`}
                </MenuItem>
                {allCameras.map((c) => {
                  const duplicateRow = rows.some(
                    (r) => r.boundSerial === c.serialNumber && r.roleKey !== editingKey,
                  );
                  const reassign = getEfemPeripheralReassignState({
                    parentGatewaySerial: c.parentGatewaySerialNumber,
                    currentGatewaySerial: gatewaySerialNumber,
                    tenantGatewayStatusBySerial,
                    blockedByLocalDraft: duplicateRow,
                    localDraftBlockLabel: ' (already on this gateway)',
                  });
                  return (
                    <MenuItem
                      key={c.serialNumber}
                      value={c.serialNumber}
                      disabled={!reassign.selectable}
                    >
                      {c.serialNumber}
                      {c.deviceModel ? ` (${c.deviceModel})` : ''}
                      {reassign.assignmentLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <AppTextField
              label="Display label (optional)"
              size="small"
              value={form.displayLabel}
              onChange={(e) => setForm((prev) => ({ ...prev, displayLabel: e.target.value }))}
              fullWidth
            />
            <Box display="flex" gap={1.5} flexWrap="wrap">
              <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                <InputLabel>Mount position</InputLabel>
                <Select
                  label="Mount position"
                  value={form.idSide}
                  onChange={(e) => setForm((prev) => ({ ...prev, idSide: e.target.value as 'top' | 'bottom' }))}
                >
                  <MenuItem value="top">Above wafer (top)</MenuItem>
                  <MenuItem value="bottom">Below wafer (bottom)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                <InputLabel>Target wafer size</InputLabel>
                <Select
                  label="Target wafer size"
                  value={form.supportedWaferSize}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, supportedWaferSize: e.target.value as dt.WAFER_SIZE }))
                  }
                >
                  {WAFER_SIZES.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <AppTextField
              label="Prealigner offset (°)"
              size="small"
              type="number"
              value={form.mountCalibrationDeg}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, mountCalibrationDeg: Number(e.target.value) }))
              }
              fullWidth
              inputProps={{ min: -360, max: 360 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setDialogOpen(false)}>Cancel</AppButton>
          <AppButton variant="contained" onClick={handleDialogApply}>
            {editingKey ? 'Apply' : 'Add'}
          </AppButton>
        </DialogActions>
      </Dialog>
    </EfemSetupSection>
  );
}
